import React, { useCallback, useEffect, useState } from 'react';
import { listTagGroups, setCustomerTags } from '../../services/tagsApi';

/*
 * Customer tags, inline under the identity header.
 *
 * Everything else on this page is a read-only projection of Epicor. Tags are
 * the exception: they are console-owned, which is the point — Epicor has no
 * field that reliably says "this is a Swimart store", so the console has to
 * hold that fact itself.
 *
 * Only groups that target customers are offered; the vocabulary is shared with
 * products and a product-only dimension has no meaning here.
 */
export default function CustomerTagsPanel({ company, custNum, tags, onChange }) {
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadVocabulary = useCallback(async () => {
    try {
      setGroups(await listTagGroups({ entity_type: 'customer' }));
    } catch (e) {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    loadVocabulary();
  }, [loadVocabulary]);

  const open = () => {
    setSelected(new Set((tags || []).map((t) => t.id)));
    setError(null);
    setEditing(true);
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await setCustomerTags(company, custNum, Array.from(selected));
      setEditing(false);
      onChange(res.tags || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const hasVocabulary = groups.some((g) => (g.values || []).length > 0);
  if (!hasVocabulary && (tags || []).length === 0) return null;

  return (
    <div className="oc-custtags">
      <span className="oc-custtags-label">Tags</span>
      {(tags || []).length === 0 ? (
        <span className="oc-muted">None</span>
      ) : (
        tags.map((tag) => (
          <span key={tag.id} className="oc-linkchip linked" title={`${tag.group_label} · ${tag.code}`}>
            {tag.label}
          </span>
        ))
      )}
      {hasVocabulary && !editing && (
        <button type="button" className="oc-btn oc-btn-secondary oc-custtags-edit" onClick={open}>
          Edit tags
        </button>
      )}

      {editing && (
        <div className="oc-custtags-editor">
          {groups.map((group) => (
            <div key={group.id} className="oc-custtags-group">
              <strong>{group.label}</strong>
              {(group.values || []).map((value) => (
                <label key={value.id} className="oc-custtags-opt">
                  <input
                    type="checkbox"
                    checked={selected.has(value.id)}
                    onChange={() => toggle(value.id)}
                  />
                  {value.label}
                </label>
              ))}
            </div>
          ))}
          {error && <p className="oc-error">{error}</p>}
          <div className="oc-custtags-actions">
            <button type="button" className="oc-btn oc-btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="button" className="oc-btn oc-btn-primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : `Save (${selected.size})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
